import React, { useEffect, useState, useCallback, useRef } from 'react';
import "../Styles/components/Notification.scss";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PlanGroupActions from "../Redux/transition/plan-group/plan-group.reducer";
import BusinessActions from "../Redux/actions/business-actions"
import DocumentActions from '../Redux/transition/document-upload/document-upload.reducer';
import PaySwitchActions from "../Redux/transition/payswitch/payswitch.reducer";
import PlanActions from "../Redux/transition/plan/plan.reducer";
import BeginOnboardingRequestActions from "../Redux/transition/beginOnboarding/beginOnboarding.reducer";
import InviteSubscriber from '../Pages/Subscriptions/Invitation/InviteSubscriber';
import { hasPendingSignatureRequest, hasSavedPspCredentials } from "../utils/pspOnboarding";
import { hasOwnProviderCredentials } from "../utils/paymentProviderStatus";

const Notification = (props) => {
    const navigate = useNavigate();
    const { t } = useTranslation("common");
    const [dialogClosed, setDialogClosed] = useState(false);

    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const planGroupsGraphedActive = useSelector((state) => state.planGroups.planGroupsGraphedActive)
    const business = useSelector((state) => state.businesses.business)
    const verifStatus = useSelector((state) => state.documents.verifStatus);
    const signingPending = useSelector((state) => state.documents.signingPending);
    const fetchingMerchantAgreements = useSelector((state) => state.documents.fetchingMerchantAgreements);
    const merchantAgreements = useSelector((state) => state.documents.merchantAgreements);
    const paymentCredentials = useSelector((state) => state.payswitch.credentials);
    const configuredPaymentCredentials = useSelector((state) => state.payswitch.paymentCredentials);
    const fetchingCredentials = useSelector((state) => state.payswitch.fetchingCredentials);
    const plans = useSelector((state) => state.plans.plans)
    const onboardingRequestStarted = useSelector((state) => state.beginOnboarding.onboardingRequestStarted);

    const dispatch = useDispatch()
    const getActivePlanGroups = useCallback((businessId) => { dispatch(PlanGroupActions.planGroupGraphedActiveRequest(businessId)) }, [dispatch])
    const getBusiness = useCallback((businessId) => { dispatch(BusinessActions.businessRequest(businessId)) }, [dispatch])
    const closeNotication = useCallback((businessId) => { dispatch(BusinessActions.businessCloseNotificationRequest(businessId)) }, [dispatch])
    const getVerificationRecord = useCallback((businessId) => { dispatch(DocumentActions.businessVerificationStatusRequest(businessId)) }, [dispatch]);
    const getMerchantAgreements = useCallback((businessId, provider) => { dispatch(DocumentActions.merchantAgreementsRequest(businessId, provider)) }, [dispatch]);
    const getCredentials = useCallback((businessId, provider) => { dispatch(PaySwitchActions.paySwitchCredentialRequest(businessId, provider)) }, [dispatch]);
    const getPlans = useCallback((businessId) => { dispatch(PlanActions.planAllRequest(businessId)) }, [dispatch])
    const getAllPlanGroups = useCallback((businessId, options) => { dispatch(PlanGroupActions.planGroupAllRequest(businessId, options)) }, [dispatch]);
    const startPaystackOnboarding = useCallback((businessId) => {
        dispatch(BeginOnboardingRequestActions.beginOnboardingRequest(businessId, "PAYSTACK"));
    }, [dispatch]);
    const paystackOnboardingBusinessId = useRef(null);
    const wasSigningPending = useRef(signingPending);
    const lastProviderRef = useRef(null);
    const suppressSigningCompleteRef = useRef(false);
    const stickyKindRef = useRef(null);
    const sawProviderLoadingRef = useRef(false);
    const [justCompletedSigning, setJustCompletedSigning] = useState(false);
    const [pollAgreementsUntil, setPollAgreementsUntil] = useState(0);
    const [awaitingProviderState, setAwaitingProviderState] = useState(false);
    const [providerCheckReady, setProviderCheckReady] = useState(false);

    const [state, setState] = useState({
        businessObject: null,
        status: null,
        verificationItem: null,
        show: false,
        close: false,
        tierVisible: false,
        duplicate: null,
        plans: [],
        verificationRecord: null,
        paymentCredentials: null
    })
    const [visibleInvite, setVisibleInvite] = useState(false);
    const [notificationDismissed, setNotificationDismissed] = useState(false);

    useEffect(() => {
        if (props.defMembership) {
            getBusiness(props.defMembership.business.id)
            getActivePlanGroups(props.defMembership.business.id)
            getAllPlanGroups(props.defMembership.business.id)
            getVerificationRecord(props.defMembership.business.id)
            getMerchantAgreements(props.defMembership.business.id, business?.pmtProvider || props.defMembership.business.pmtProvider)
            getCredentials(props.defMembership.business.id, props.defMembership.business.pmtProvider)
            getPlans(defMembership.business.id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.defMembership])

    useEffect(() => {
        if (props.defMembership && business?.pmtProvider) {
            const businessId = props.defMembership.business.id;
            getCredentials(businessId, business.pmtProvider)
            getMerchantAgreements(businessId, business.pmtProvider)
            const previousProvider = lastProviderRef.current;
            if (previousProvider && previousProvider !== business.pmtProvider) {
                suppressSigningCompleteRef.current = true;
                stickyKindRef.current = null;
                sawProviderLoadingRef.current = false;
                setProviderCheckReady(false);
                setJustCompletedSigning(false);
                setNotificationDismissed(false);
                setAwaitingProviderState(true);
                setPollAgreementsUntil(Date.now() + 60000);
            }
            lastProviderRef.current = business.pmtProvider;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [business?.pmtProvider, props.defMembership])

    useEffect(() => {
        const membership = props.defMembership;
        if (!membership?.business?.id || !business) return;
        if (business.customPmtProvider || business.verified) return;
        if (paystackOnboardingBusinessId.current === membership.business.id) return;
        paystackOnboardingBusinessId.current = membership.business.id;
        startPaystackOnboarding(membership.business.id);
    }, [props.defMembership, business, startPaystackOnboarding]);

    useEffect(() => {
        if (onboardingRequestStarted && props.defMembership) {
            const businessId = props.defMembership.business.id;
            const provider = business?.pmtProvider || props.defMembership.business.pmtProvider;
            getVerificationRecord(businessId);
            getBusiness(businessId);
            getMerchantAgreements(businessId, provider);
            getCredentials(businessId, provider);
            suppressSigningCompleteRef.current = true;
            setJustCompletedSigning(false);
            if (business?.customPmtProvider) {
                stickyKindRef.current = null;
                sawProviderLoadingRef.current = false;
                setProviderCheckReady(false);
                setAwaitingProviderState(true);
                setPollAgreementsUntil(Date.now() + 60000);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onboardingRequestStarted])

    useEffect(() => {
        if (hasPendingSignatureRequest(signingPending)) {
            suppressSigningCompleteRef.current = false;
        }
        if (wasSigningPending.current && !signingPending && !suppressSigningCompleteRef.current) {
            setJustCompletedSigning(true);
            setNotificationDismissed(false);
        }
        wasSigningPending.current = signingPending;
    }, [signingPending]);

    useEffect(() => {
        if (!awaitingProviderState) return;
        if (fetchingMerchantAgreements || fetchingCredentials) return;
        setAwaitingProviderState(false);
    }, [awaitingProviderState, fetchingMerchantAgreements, fetchingCredentials]);

    useEffect(() => {
        const loading = fetchingMerchantAgreements || fetchingCredentials || awaitingProviderState;
        if (loading) {
            sawProviderLoadingRef.current = true;
            return;
        }
        if (sawProviderLoadingRef.current) {
            setProviderCheckReady(true);
        }
    }, [fetchingMerchantAgreements, fetchingCredentials, awaitingProviderState]);

    useEffect(() => {
        if (!props.defMembership) return;
        const shouldPoll = hasPendingSignatureRequest(signingPending) || Date.now() < pollAgreementsUntil;
        if (!shouldPoll) return;
        const businessId = props.defMembership.business.id;
        const provider = business?.pmtProvider || props.defMembership.business.pmtProvider;
        const refreshAgreements = () => getMerchantAgreements(businessId, provider);
        const interval = setInterval(() => {
            if (hasPendingSignatureRequest(signingPending) || Date.now() < pollAgreementsUntil) {
                refreshAgreements();
            }
        }, 15000);
        const onFocus = () => {
            if (hasPendingSignatureRequest(signingPending) || Date.now() < pollAgreementsUntil) {
                refreshAgreements();
            }
        };
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signingPending, pollAgreementsUntil, props.defMembership, business?.pmtProvider])

    useEffect(() => {
        if (business) {
            setState((state) => { return { ...state, businessObject: business } })
        }
        if (planGroupsGraphedActive) {
            setState((state) => { return { ...state, plans: planGroupsGraphedActive?.groups } })
        }
        if (paymentCredentials) {
            setState((state) => { return { ...state, paymentCredentials: paymentCredentials } })
        }
        if (!paymentCredentials && !fetchingCredentials) {
            setState((state) => { return { ...state, paymentCredentials: null } })
        }
        if (verifStatus) {
            setState((state) => { return { ...state, verificationRecord: verifStatus } })
        }
        if (!verifStatus) {
            setState((state) => { return { ...state, verificationRecord: null } })
        }
        if (props.hide) {
            localStorage.setItem('verificationItem', JSON.stringify(props.hide));
            setState((state) => ({ ...state, close: true }))
        }
        if (state.close) {
            setTimeout(JSON.parse(localStorage.getItem('verificationItem')), 4000)
        }
        const dialogClosed = JSON.parse(localStorage.getItem('dialogClosed'));
        setDialogClosed(dialogClosed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.hide, business, state.close, dialogClosed, planGroupsGraphedActive, paymentCredentials, verifStatus, fetchingCredentials])

    useEffect(() => {
        const dialogClosed = JSON.parse(localStorage.getItem('dialogClosed'));
        if ((!props.fetchingBusiness || !props.fetchingVerifStatus) && (!dialogClosed && state.businessObject?.verified && plans?.length < 1)) {
            setState((state) => ({ ...state, tierVisible: true }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCloseNotification = () => {
        setJustCompletedSigning(false);
        setNotificationDismissed(true);
        closeNotication(props.defMembership.business.id);
    };

    const toggleInvite = () => {
        setVisibleInvite(!visibleInvite)
    }

    const renderSigningPendingBanner = (businessData) => {
        if (!hasPendingSignatureRequest(signingPending)) return null;
        const signerName = businessData?.signerFullName || t("idoc.signing_pending_signatory");
        const signerEmail = businessData?.signerEmail || "";
        if (!businessData?.signerFullName && !businessData?.signerEmail) return null;

        return (
            <div className="in_review signing_pending_banner">
                <div className="flex" style={{ alignItems: "center", width: "100%" }}>
                    <p className="p-message-summary-in-review mb-0 mt-0">
                        <Trans
                            i18nKey="idoc.signing_pending_banner"
                            values={{ name: signerName, email: signerEmail }}
                            components={{
                                1: <strong />,
                                2: <strong />,
                                3: <strong />,
                            }}
                        />
                    </p>
                </div>
            </div>
        );
    };

    const withSigningBanner = (_businessData, content) => content;

    const renderDefaultPaystackBanner = () => {
        const canManagePayments = defMembership?.role === "OWNER" || defMembership?.role === "ADMIN";
        return (
            <div className="in_review">
                <div className="p-message-wrapper p-d-flex">
                    {canManagePayments ?
                        <p className="p-message-summary-in-review">
                            <Trans i18nKey="idoc.not_verified"
                                components={{
                                    1: <span onClick={() =>
                                        window.open('https://support.paystack.com/en/articles/2125634', '_blank')}
                                        className="select_provider"></span>,
                                    2: <span onClick={() => {
                                        navigate({
                                            pathname: "/payment_providers/payStack"
                                        })
                                    }} className="select_provider"></span>
                                }}>
                            </Trans>
                        </p> :
                        <p className="p-message-summary-in-review">{t("idoc.not_verified_viewer")}</p>}
                </div>
            </div>
        );
    };

    const renderLinkApiKeyBanner = (businessData) => {
        const canManagePayments = defMembership?.role === "OWNER" || defMembership?.role === "ADMIN";
        const provider = businessData?.pmtProvider || defMembership?.business?.pmtProvider || "PAYSTACK";
        const providerRoutes = {
            PAYSTACK: "/payment_providers/paystack",
            PAYSWITCH: "/payment_providers/paySwitch",
            APPSNMOBILE: "/payment_providers/appsNmobile",
            JUNIPAY: "/payment_providers/junipay",
            ALPAY: "/payment_providers/alpay",
        };
        return (
            <div className="in_review">
                <div className="p-message-wrapper p-d-flex">
                    {canManagePayments ?
                        <p className="p-message-summary-in-review">
                            <Trans i18nKey="idoc.link_api_key_banner"
                                components={{
                                    1: <span
                                        onClick={() => navigate({ pathname: providerRoutes[provider] || "/payment_providers" })}
                                        className="select_provider"></span>
                                }}>
                            </Trans>
                        </p> :
                        <p className="p-message-summary-in-review">{t("idoc.not_saved_viewer1")} {t("idoc.not_saved_viewer2")}</p>}
                </div>
            </div>
        );
    };

    const renderRegistrationCompleteBanner = () => (
        <div className="verified_notice">
            <div className="flex" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <p className="mb-0 mt-0 p-message-summary-verified">
                    <Trans
                        i18nKey="idoc.registration_complete"
                        components={{
                            1: <strong />,
                        }}
                    />
                </p>
                {defMembership && (
                    <i
                        className="pi pi-times-circle"
                        style={{ marginLeft: "auto", fontSize: "1rem", cursor: "pointer" }}
                        onClick={handleCloseNotification}
                    />
                )}
            </div>
        </div>
    );

    const checks = (businessData) => {
        const isBusinessVerified = businessData?.verified
        const customPmtProviderEnabled = businessData?.customPmtProvider;
        const liveCredentials = paymentCredentials ?? state.paymentCredentials;
        const currentProvider = businessData?.pmtProvider || defMembership?.business?.pmtProvider;
        const currentProviderKeysLinked = hasOwnProviderCredentials(liveCredentials, currentProvider);
        const credentialsSaved = hasSavedPspCredentials({
            credentials: liveCredentials,
            paymentCredentials: configuredPaymentCredentials,
        }) || currentProviderKeysLinked;
        const paystackKeysLinked = hasOwnProviderCredentials(liveCredentials, "PAYSTACK");
        const hasAgreement = Array.isArray(merchantAgreements) && merchantAgreements.length > 0;
        const agreementSigned = !fetchingMerchantAgreements && ((hasAgreement && !signingPending) || justCompletedSigning);
        const registrationAcknowledged = Boolean(businessData?.verifAcknowledgedOn) && !justCompletedSigning;

        // 4. Agreement signed and credentials saved
        if (agreementSigned && credentialsSaved && !registrationAcknowledged) {
            return renderRegistrationCompleteBanner();
        }

        // 3. Agreement signed, credentials still missing.
        // Empty credentials while a fetch is in flight are not a result yet.
        if (agreementSigned && !credentialsSaved && providerCheckReady && !fetchingCredentials && !fetchingMerchantAgreements) {
            return renderLinkApiKeyBanner(businessData);
        }

        // 1. Not signed and no credentials yet
        if (!isBusinessVerified && customPmtProviderEnabled && !credentialsSaved) {
            return <>
                <div className="not_verified_notice">
                    <div className="p-message-wrapper p-d-flex">
                        <div className="p-message-wrapper p-d-flex">
                            {(defMembership?.role === "OWNER" || defMembership?.role === "ADMIN") ?
                                <p className="p-message-summary">
                                    <Trans i18nKey="idoc.not_verified_main"
                                        components={{
                                            1: <span onClick={() => {
                                                navigate({
                                                    pathname: "/payment_providers"
                                                })
                                            }} className="select_provider"></span>
                                        }}>
                                    </Trans>
                                </p> :
                                <>
                                    <p style={{ textDecoration: "none", color: " #d0021b" }}>{t("idoc.not_verified_viewer")}</p>
                                </>}
                        </div>
                    </div>
                </div >
            </>
        }

        //if unverified business, customPSP disabled and no Paystack keys linked
        if (!isBusinessVerified && !customPmtProviderEnabled && !paystackKeysLinked) {
            return renderDefaultPaystackBanner();
        }


        //if verified business, customPSP disabled and payment provider isn't the DEFAULT(paystack)
        if (isBusinessVerified && liveCredentials?.provider !== "PAYSTACK" && !customPmtProviderEnabled && !agreementSigned) {
            return renderDefaultPaystackBanner();
        }



        //if verified business, customPSP disabled and payment provider is the DEFAULT(paystack)
        if (isBusinessVerified && liveCredentials?.provider === "PAYSTACK" && !customPmtProviderEnabled && !businessData?.verifAcknowledgedOn) {
            return withSigningBanner(businessData, (
            <div className="verified_notice">
                <div className="flex" style={{ justifyContent: "space-between" }}>
                    <p className="mb-0 mt-0 p-message-summary-verified">
                        <Trans
                            i18nKey="idoc.verified_account"
                            components={{
                                1: <span className="active_txt" />,
                                2: signingPending ? <span /> : <span className="go_ahead_txt"></span>,
                            }}
                        >
                        </Trans>
                    </p>
                    {(defMembership) && !signingPending && (
                        <>
                            <Trans
                                i18nKey="idoc.share_business_or_invite_subs"
                                components={{
                                    1: <p onClick={() => {
                                        navigate({
                                            pathname: "/share/business",
                                        })
                                    }}
                                        className='share_business_txt'></p>,
                                    2: <p className='mb-0 mt-0 p-message-summary-verified'></p>,
                                    3: <p
                                        onClick={() => {
                                            setVisibleInvite(true)
                                        }}
                                        className='invite_subs_txt'></p>
                                }}>
                            </Trans>
                        </>
                    )}
                    {(defMembership) && (
                        < i className="pi pi-times-circle"
                            style={{ marginLeft: "auto", fontSize: "1rem", cursor: "pointer" }}
                            onClick={handleCloseNotification} />
                    )}
                    <div>
                    </div>
                </div>
            </div >
            ));
        }


        if (isBusinessVerified && liveCredentials && plans?.length < 1 && businessData.verifAcknowledgedOn === null) {
            return withSigningBanner(businessData, (
            <div>
                <div className="verified_notice">
                    <div className="flex" style={{ justifyContent: "space-between" }}>
                        <p className="mb-0 mt-0 p-message-summary-verified">
                            <Trans
                                i18nKey="idoc.verified_account_create_plan"
                                components={{
                                    1: <span className="active_txt" />,
                                    2: (defMembership?.role === "OWNER" || defMembership?.role === "ADMIN") ? (
                                        <span
                                            onClick={() => {
                                                const groupId = planGroupsGraphedActive?.groups?.[0].group.id
                                                navigate(`/products/plan/new/${groupId}`)
                                            }}
                                            className="create_plan_link">
                                        </span>
                                    ) : <span></span>
                                }}
                            >
                            </Trans>
                        </p>


                        {(defMembership?.role === "OWNER" || defMembership?.role === "ADMIN") && (
                            <i className="pi pi-times-circle"
                                style={{ marginLeft: "auto", fontSize: "1rem", cursor: "pointer" }}
                                onClick={handleCloseNotification} />
                        )}
                        <div>
                        </div>
                    </div>
                </div>
            </div >
            ));
        }
        if (isBusinessVerified && !credentialsSaved && businessData.verifAcknowledgedOn === null) {
            return <div>
                <div className="verified_notice">
                    <div className="flex" style={{ justifyContent: "space-between" }}>
                        <p className="mb-0 mt-0 p-message-summary-verified">
                            <Trans
                                i18nKey="idoc.not_fully_successful_verification"
                                components={{
                                    1: <span className="not_fully_verified" />,
                                    2: (defMembership?.role === "OWNER" || defMembership?.role === "ADMIN") ?
                                        <span
                                            onClick={() => {
                                                defMembership.business.pmtProvider === "PAYSWITCH" ?
                                                    navigate({ pathname: "/payment_providers/paySwitch" })
                                                    : navigate({ pathname: "/payment_providers/appsNmobile" })
                                            }}
                                            className='not_fully_verified_txt'
                                        ></span> : <span></span>
                                }}
                            >
                            </Trans>
                        </p>
                        {(defMembership?.role === "OWNER" || defMembership?.role === "ADMIN") && (
                            <i className="pi pi-times-circle"
                                style={{ marginLeft: "auto", fontSize: "1rem", cursor: "pointer" }}
                                onClick={handleCloseNotification} />
                        )}
                        <div>
                        </div>
                    </div>
                </div>
            </div >
        }

        if (isBusinessVerified && liveCredentials && !businessData.verifAcknowledgedOn) {
            return withSigningBanner(businessData, (
            <div className="verified_notice">
                <div className="flex" style={{ justifyContent: "space-between" }}>
                    <p className="mb-0 mt-0 p-message-summary-verified">
                        <Trans
                            i18nKey="idoc.verified_account"
                            components={{
                                1: <span className="active_txt" />,
                                2: signingPending ? <span /> : <span className="go_ahead_txt"></span>,
                            }}
                        >
                        </Trans>
                    </p>
                    {(defMembership) && !signingPending && (
                        <>
                            <Trans
                                i18nKey="idoc.share_business_or_invite_subs"
                                components={{
                                    1: <p onClick={() => {
                                        navigate({
                                            pathname: "/share/business",
                                        })
                                    }}
                                        className='share_business_txt'></p>,
                                    2: <p className='mb-0 mt-0 p-message-summary-verified'></p>,
                                    3: <p
                                        onClick={() => {
                                            setVisibleInvite(true)
                                        }}
                                        className='invite_subs_txt'></p>
                                }}>
                            </Trans>
                        </>
                    )}
                    {(defMembership) && (
                        < i className="pi pi-times-circle"
                            style={{ marginLeft: "auto", fontSize: "1rem", cursor: "pointer" }}
                            onClick={handleCloseNotification} />
                    )}
                    <div>
                    </div>
                </div>
            </div >
            ));
        }
    }

    const businessData = state.businessObject || business || props.defMembership?.business;
    const currentProvider = businessData?.pmtProvider || defMembership?.business?.pmtProvider;
    const hasSigner = Boolean(businessData?.signerFullName || businessData?.signerEmail);
    const signingReady = hasPendingSignatureRequest(signingPending) && hasSigner;
    const liveCredentials = paymentCredentials ?? state.paymentCredentials;
    const currentProviderKeysLinked = hasOwnProviderCredentials(liveCredentials, currentProvider);
    const needsProviderKeys = Boolean(
        currentProvider &&
        !currentProviderKeysLinked &&
        (businessData?.customPmtProvider || currentProvider !== "PAYSTACK")
    );
    const providerStateLoading = fetchingMerchantAgreements || fetchingCredentials || awaitingProviderState;

    if (currentProvider && stickyKindRef.current && lastProviderRef.current && lastProviderRef.current !== currentProvider) {
        stickyKindRef.current = null;
    }

    let bannerKind = null;
    if (signingReady) {
        bannerKind = "signing";
    } else if (!providerCheckReady) {
        // First credential/agreement check has not finished. Do not show "link key"
        // from empty credentials and then hide it when the result arrives.
        bannerKind = null;
    } else if (providerStateLoading) {
        bannerKind = stickyKindRef.current;
    } else if (needsProviderKeys) {
        bannerKind = "linkKey";
    } else {
        bannerKind = "fallback";
    }

    if (stickyKindRef.current === "signing" && bannerKind !== "signing" && providerStateLoading) {
        bannerKind = "signing";
    }

    if (bannerKind && providerCheckReady && !providerStateLoading) {
        stickyKindRef.current = bannerKind;
    } else if (bannerKind === "signing") {
        stickyKindRef.current = "signing";
    }

    let notice = null;
    if (bannerKind === "signing") {
        notice = renderSigningPendingBanner(businessData);
    } else if (bannerKind === "linkKey") {
        notice = renderLinkApiKeyBanner(businessData);
    } else if (bannerKind === "fallback" && businessData && !notificationDismissed) {
        notice = checks(businessData);
    }

    return (
        <>
            <div>
                {notice || (!businessData ? <div></div> : null)}
                <InviteSubscriber visible={visibleInvite} toggleVisible={visibleInvite} setVisibleInvite={setVisibleInvite} onHide={toggleInvite} initialTier={null} />
            </div >
        </>
    )
}
export default Notification;